import { CONTEXT_TYPES } from '@/store/mutation-types'
import { Module, MutationTree } from 'vuex'
import { existsInObject, isUserMobile } from '@/utils/helpers'
import { polishParameter } from '@/utils/setup'
import { actions } from './actions'
import { getters } from './getters'
import { RootState } from 'types'
import { ContextState, User, ShippingAddress } from './contextTypes'
import { ProviderType } from '@/utils/identifyFields/providers/providerTypes'
import { uuid } from 'uuidv4'

function initUserData (userData: any, wizardData: object): User {
  return {
    userId: uuid(), // if manual register then we need to create a unique userId
    // userId: 'PayTestSE', // if manual register then we need to create a unique userId
    email: '',
    zip: '',
    nationalId: '',
    address1: '',
    address2: '',
    city: '',
    firstName: '',
    lastName: '',
    phonenumber: '',
    ...userData,
    ...wizardData
  }
}

function initShippingData (shippingData: any): object {
  return {
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    zip: '',
    city: '',
    ...shippingData
  }
}

export const state: ContextState = {
  userDeviceExists: null,
  user: undefined,
  manualUserRegister: false,
  editUserData: {},
  editUserErrors: [],
  userWizardData: {},
  customShippingAddress: false,
  shippingAddressData: undefined,
  editShippingAddressData: {},
  editShippingErrors: [],
  identifyWizardStep: 0, // start out on 0 meaning show 1 field
  wizardIdentifyProvider: null, // the config object of the current provider object selected (bankId, frejaId etc) (object)
  settings: {
    rememberDevice: true, // save deviceId and link it to user's data in source DB?
    storeData: true // store the user's data in source DB?
  },
  didInitialAuth: null,
  didAccessToken: null,
  didIdToken: null,
  messages: undefined,
  isLoading: false,
  initLoading: true,
  waitingForIdentifyProvider: false,
  errors: {
    main: null
  },
  containerWidth: 450,
  isMobile: isUserMobile(),
  config: {
    merchantId: '1000',
    didClientId: null,
    checkUserDevice: true,
    hostUri: null,
    environment: 'test',
    termsConditionsUrl: null,
    editUser: false,
    identifyFields: ['zip', 'email', 'phonenumber'],
    identifyProvider: null, // bankId, frejaId etc (string)
    country: 'SE',
    owner: 'Bambora',
    source: undefined,
    loginTimeout: 5, // 5min
    locale: 'sv_SE'
  }
}

const mutations: MutationTree<ContextState> = {
  [CONTEXT_TYPES.SET_CONFIG] (state, config: object) {
    for (const key in config) {
      if (existsInObject(key, state.config)) {
        state.config[key] = polishParameter(config[key])
      }
    }
  },
  [CONTEXT_TYPES.SET_USER_DEVICE_EXISTS] (state, exists) {
    state.userDeviceExists = exists
  },
  [CONTEXT_TYPES.SET_CHECK_USER_DEVICE] (state, checkUserDevice) {
    state.config.checkUserDevice = checkUserDevice
  },
  [CONTEXT_TYPES.TRIGGER_USER_MANUAL_REGISTER] (state, triggerNewUser) {
    state.manualUserRegister = triggerNewUser
  },
  [CONTEXT_TYPES.SET_ERROR] (state, obj) {
    const { error, key, translate } = obj
    state.errors[key] = {
      error: error,
      translate
    }
  },
  [CONTEXT_TYPES.RESET_ERRORS] (state) {
    state.errors = {
      main: null
    }
  },
  [CONTEXT_TYPES.SET_DID_INITIAL_AUTH] (state, { codeVerifier, codeChallenge }) {
    state.didInitialAuth = {
      didCodeVerifier: codeVerifier,
      didCodeChallenge: codeChallenge
    }
  },
  [CONTEXT_TYPES.SET_DID_AUTH_TOKENS] (state, authTokens) {
    state.didAccessToken = authTokens.access_token
    state.didIdToken = authTokens.id_token
  },
  [CONTEXT_TYPES.SET_EDIT_USER] (state, editUser) {
    state.editUserData = initUserData(state.user, state.userWizardData)
    state.config.editUser = editUser
  },
  [CONTEXT_TYPES.SET_EDIT_USER_ERRORS] (state, errorFields) {
    state.editUserErrors = errorFields
  },
  [CONTEXT_TYPES.SET_EDIT_USER_DATA] (state, { key, value }) {
    state.editUserData[key] = value
  },
  [CONTEXT_TYPES.RESET_EDIT_USER_DATA] (state) {
    state.editUserData = {}
  },
  [CONTEXT_TYPES.SET_USER_WIZARD_DATA] (state, payload: any) {
    const { key, value } = payload
    state.userWizardData = <object>{
      ...state.userWizardData,
      [key]: value
    }
  },
  [CONTEXT_TYPES.SET_IDENTIFY_WIZARD_STEP] (state, step: number) {
    state.identifyWizardStep = step
  },
  [CONTEXT_TYPES.SET_IDENTIFY_PROVIDER] (state, providerId: string) {
    state.config.identifyProvider = providerId
  },
  [CONTEXT_TYPES.SET_WAITING_FOR_IDENTIFY_PROVIDER] (state, isWaiting: boolean) {
    state.waitingForIdentifyProvider = isWaiting
  },
  [CONTEXT_TYPES.SET_WIZARD_FIELDS] (state, fields: Array<string>) {
    state.config.identifyFields = fields
  },
  [CONTEXT_TYPES.SET_WIZARD_PROVIDER_OBJECT] (state, providerObject: ProviderType) {
    state.config.identifyProvider = providerObject.id
    state.wizardIdentifyProvider = providerObject
  },
  [CONTEXT_TYPES.SET_USER_DATA] (state, user: User) {
    state.user = user
    state.userWizardData = {}
  },
  [CONTEXT_TYPES.SET_SHIPPING_DATA] (state, shippingAddress: ShippingAddress) {
    state.shippingAddressData = shippingAddress
    state.editShippingAddressData = {}
  },
  [CONTEXT_TYPES.SET_EDIT_SHIPPING] (state) {
    state.editShippingAddressData = initShippingData(state.shippingAddressData)
    state.shippingAddressData = undefined
    state.customShippingAddress = true
  },
  [CONTEXT_TYPES.SET_EDIT_SHIPPING_DATA] (state, { key, value }) {
    state.editShippingAddressData[key] = value
  },
  [CONTEXT_TYPES.RESET_SHIPPING_DATA] (state) {
    state.editShippingAddressData = {}
    state.shippingAddressData = undefined
    state.customShippingAddress = false
  },
  [CONTEXT_TYPES.RESET_IDENTIFIED_USER] (state, identifyProvider: string) {
    if (identifyProvider === 'manual') {
      state.config.identifyProvider = 'bankId'
    }
    state.identifyWizardStep = 0
    state.userWizardData = {}
    state.editUserData = {}
    state.config.editUser = false
    state.user = undefined
  },
  [CONTEXT_TYPES.SET_I18N_MESSAGES] (state, messages: object) {
    state.messages = messages
  },
  [CONTEXT_TYPES.TOGGLE_REMEMBER_DEVICE] (state) {
    const newRememberMeState = !state.settings.rememberDevice
    localStorage.setItem(`piq_${state.config.country}_rememberDevice`, JSON.stringify(newRememberMeState))
    state.settings.rememberDevice = newRememberMeState
  },
  [CONTEXT_TYPES.TOGGLE_STORE_DATA] (state) {
    const newStoreDataState = !state.settings.storeData
    localStorage.setItem(`piq_${state.config.country}_storeData`, JSON.stringify(newStoreDataState))
    state.settings.storeData = newStoreDataState
  },
  [CONTEXT_TYPES.SET_INIT_IS_LOADING] (state, isLoading) {
    state.initLoading = isLoading
  },
  [CONTEXT_TYPES.SET_IS_LOADING] (state, isLoading) {
    state.isLoading = isLoading
  }
}

const namespaced: boolean = true

export const context: Module<ContextState, RootState> = {
  namespaced,
  state,
  actions,
  getters,
  mutations
}
